import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { MagnifyingGlass, GearSix, EyeSlash, Buildings, CaretDown, CaretUp, Globe } from "@phosphor-icons/react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODES = [
  { key: "default", label: "Default" },
  { key: "show", label: "Show" },
  { key: "hide", label: "Hide" },
];

export default function AdminSettingsPage() {
  const [orgs, setOrgs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [globalHidden, setGlobalHidden] = useState(new Set());
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [orgsRes, catsRes, pricingRes] = await Promise.all([
        axios.get(`${API}/admin/orgs?status=active`, { withCredentials: true }),
        axios.get(`${API}/categories`, { withCredentials: true }),
        axios.get(`${API}/admin/settings/pricing`, { withCredentials: true }),
      ]);
      setOrgs(orgsRes.data.organizations || []);
      setCategories(catsRes.data.categories || []);
      setGlobalHidden(new Set(pricingRes.data.global_hidden_categories || []));
    } catch { toast.error("Failed to load settings"); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const saveGlobal = async (nextSet) => {
    const prev = globalHidden;
    setGlobalHidden(nextSet);
    try {
      await axios.put(`${API}/admin/settings/pricing`, { hidden_categories: [...nextSet] }, { withCredentials: true });
      toast.success("Global pricing defaults updated");
    } catch {
      toast.error("Failed to update");
      setGlobalHidden(prev);
    }
  };

  const toggleGlobalCat = (key) => {
    const next = new Set(globalHidden);
    if (next.has(key)) next.delete(key); else next.add(key);
    saveGlobal(next);
  };

  const patchOrg = async (org, payload, optimistic) => {
    setSavingId(org.id);
    setOrgs(prev => prev.map(o => o.id === org.id ? { ...o, ...optimistic } : o));
    try {
      await axios.put(`${API}/admin/orgs/${org.id}`, payload, { withCredentials: true });
    } catch {
      toast.error("Failed to update setting");
      setOrgs(prev => prev.map(o => o.id === org.id ? org : o));
    }
    setSavingId(null);
  };

  const toggleHideAll = (org, hide) => {
    patchOrg(org, { hide_pricing: hide }, { hide_pricing: hide });
    toast.success(hide ? `All pricing hidden for ${org.name}` : `Pricing restored for ${org.name}`);
  };

  const setOrgCatMode = (org, key, mode) => {
    const overrides = { ...(org.pricing_overrides || {}) };
    if (mode === "default") delete overrides[key]; else overrides[key] = mode;
    patchOrg(org, { pricing_overrides: overrides }, { pricing_overrides: overrides });
  };

  const orgCatMode = (org, key) => (org.pricing_overrides || {})[key] || "default";

  // Effective hidden state for a clinic+category (for the status hint)
  const isEffectivelyHidden = (org, key) => {
    if (org.hide_pricing) return true;
    const mode = orgCatMode(org, key);
    if (mode === "hide") return true;
    if (mode === "show") return false;
    return globalHidden.has(key);
  };

  const filtered = orgs.filter(o => o.name?.toLowerCase().includes(search.toLowerCase()) || (o.code || "").includes(search));

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-settings-page">
      <div className="mb-6">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">ADMIN</p>
        <h1 className="font-[Chivo] text-3xl sm:text-4xl font-black tracking-tighter text-zinc-950 mt-1 flex items-center gap-3">
          <GearSix size={32} weight="fill" /> Settings
        </h1>
      </div>

      {/* Global category pricing defaults */}
      <div className="border border-zinc-200 mb-8" data-testid="global-pricing-section">
        <div className="p-5 border-b border-zinc-200 bg-zinc-50">
          <div className="flex items-center gap-2">
            <Globe size={20} className="text-zinc-700" />
            <h2 className="font-[Chivo] font-bold text-lg text-zinc-950">Global Pricing Defaults</h2>
          </div>
          <p className="text-sm text-zinc-500 mt-1">Hide catalog pricing for an entire category across <strong>all clinics</strong>. Hidden items become quote-only — clinics can still order them, but a price is requested rather than shown. Individual clinics can override these below.</p>
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-zinc-500 font-mono">LOADING...</div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {categories.map(c => (
              <div key={c.key} className="flex items-center justify-between gap-4 p-4" data-testid={`global-cat-row-${c.key}`}>
                <div>
                  <p className="text-sm font-semibold text-zinc-950">{c.label}</p>
                  <p className="text-xs font-mono text-zinc-400">{c.key}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-xs font-mono uppercase tracking-wider ${globalHidden.has(c.key) ? "text-amber-600" : "text-zinc-400"}`}>
                    {globalHidden.has(c.key) ? "Hidden" : "Visible"}
                  </span>
                  <Switch
                    checked={globalHidden.has(c.key)}
                    onCheckedChange={() => toggleGlobalCat(c.key)}
                    data-testid={`global-hide-switch-${c.key}`}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Per-clinic pricing visibility */}
      <div className="border border-zinc-200" data-testid="pricing-visibility-section">
        <div className="p-5 border-b border-zinc-200 bg-zinc-50">
          <div className="flex items-center gap-2">
            <EyeSlash size={20} className="text-zinc-700" />
            <h2 className="font-[Chivo] font-bold text-lg text-zinc-950">Per-Clinic Pricing Visibility</h2>
          </div>
          <p className="text-sm text-zinc-500 mt-1">Override pricing visibility for a specific clinic — hide all pricing, or set category-level overrides (Default inherits the global setting above).</p>
        </div>

        <div className="p-4 border-b border-zinc-200">
          <div className="relative">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              placeholder="Search clinics by name or code..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-10 rounded-none"
              data-testid="settings-search-input"
            />
          </div>
        </div>

        {loading ? (
          <div className="p-12 text-center text-sm text-zinc-500 font-mono">LOADING...</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-sm text-zinc-500">No active clinics found</div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {filtered.map(org => (
              <div key={org.id} data-testid={`org-pricing-row-${org.id}`}>
                <div className="flex items-center justify-between gap-4 p-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 flex items-center justify-center bg-zinc-100 border border-zinc-200 flex-shrink-0">
                      <Buildings size={18} className="text-zinc-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-zinc-950 truncate">{org.name}</p>
                      <p className="text-xs font-mono text-zinc-400">{org.code || "—"} · {org.member_count} member(s)</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 flex-shrink-0">
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-mono uppercase tracking-wider ${org.hide_pricing ? "text-amber-600" : "text-zinc-400"}`}>Hide all</span>
                      <Switch
                        checked={!!org.hide_pricing}
                        onCheckedChange={(v) => toggleHideAll(org, v)}
                        disabled={savingId === org.id}
                        data-testid={`hide-pricing-switch-${org.id}`}
                      />
                    </div>
                    <button
                      onClick={() => setExpanded(expanded === org.id ? null : org.id)}
                      className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wider text-zinc-500 hover:text-zinc-950 transition-colors"
                      data-testid={`toggle-overrides-${org.id}`}
                    >
                      Categories {expanded === org.id ? <CaretUp size={14} /> : <CaretDown size={14} />}
                    </button>
                  </div>
                </div>

                {expanded === org.id && (
                  <div className="px-4 pb-4 bg-zinc-50/60 border-t border-zinc-100" data-testid={`overrides-panel-${org.id}`}>
                    {org.hide_pricing ? (
                      <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 p-3 mt-3">"Hide all" is on — all categories are hidden for this clinic. Turn it off to use category overrides.</p>
                    ) : (
                      <div className="mt-3 space-y-2">
                        {categories.map(c => {
                          const mode = orgCatMode(org, c.key);
                          const hidden = isEffectivelyHidden(org, c.key);
                          return (
                            <div key={c.key} className="flex items-center justify-between gap-3" data-testid={`org-cat-override-${org.id}-${c.key}`}>
                              <div className="min-w-0">
                                <p className="text-sm text-zinc-800 truncate">{c.label}</p>
                                <p className={`text-[10px] font-mono uppercase tracking-wider ${hidden ? "text-amber-600" : "text-zinc-400"}`}>{hidden ? "Pricing hidden" : "Pricing visible"}</p>
                              </div>
                              <div className="flex border border-zinc-300 bg-white flex-shrink-0">
                                {MODES.map(m => (
                                  <button
                                    key={m.key}
                                    onClick={() => setOrgCatMode(org, c.key, m.key)}
                                    disabled={savingId === org.id}
                                    className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider transition-colors disabled:opacity-50 ${
                                      mode === m.key ? "bg-zinc-950 text-white" : "bg-white text-zinc-600 hover:bg-zinc-100"
                                    }`}
                                    data-testid={`override-${org.id}-${c.key}-${m.key}`}
                                  >
                                    {m.label}
                                  </button>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
